import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import { GridSettingType } from '@apply/constants';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LevelOperatorType } from '../../assessment/common/constants/LevelOperatorType';
import './experiencelevel.css';
import { handleAdminError } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';

const BfiLevelReferenceTable = ({ principalId, gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);
    const [currentPrincipalLevel, set_currentPrincipalLevel] = useState('');
    const [currentPrincipalLevelName, set_currentPrincipalLevelName] = useState('');

    const dataFunction = (criteria) => experienceApi.getPrincicipalExperienceLevels(criteria);

    const styles = {
        headerIcon: {
            fontSize: '1.5rem',
            fontWeight: '100',
            color: '#2196f3',
        },
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.experience);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    useEffect(() => {
        async function fetchCurrentLevelData() {
            //make call to level for this principal
            const response = await experienceApi.getExperienceLevel(principalId);
            if (response.statusCode !== 200) {
                handleAdminError(new Error('Unable to determine Current Level.'), 'Unable to determine Current Level.');
                return;
            }

            const levelResult = response.result;
            if (levelResult) {
                set_currentPrincipalLevel(levelResult.id);
                set_currentPrincipalLevelName(levelResult.levelName);
            }
        }

        fetchCurrentLevelData();
    }, [principalId]);

    const levelNameRender = (e) => {
        return <div>{e.data.levelName.toUpperCase()}</div>;
    };

    const qualificationsHeaderRender = (e) => {
        return <h2>QUALIFICATIONS</h2>;
    };

    const loanSizeHeaderRender = (e) => {
        return <h2>LOAN SIZE</h2>;
    };

    const soldHomesHeaderRender = (e) => {
        return (
            <div>
                <h6>Sold Homes</h6>
                <div>Since 2014</div>
            </div>
        );
    };

    const bfiRepaymentsRender = (e) => {
        return (
            <div>
                <h6>BFI Repayments</h6>
                <div>Last 5 Years</div>
            </div>
        );
    };

    const loanSizeMaxLTCHeaderRender = (e) => {
        return (
            <div>
                <h6>Maximum LTC</h6>
                <div>100% if land sub (level 1-5, 7)</div>
            </div>
        );
    };

    const loanSizeMaxLTVHeaderRender = (e) => {
        return (
            <div>
                <h6>Maximum LTV</h6>
                <div>loans {'>'} 1MM per unit are lower**</div>
            </div>
        );
    };

    const unifierCellRender = (e) => {
        if (e.data.soldHomes !== null && e.data.bfiRepayments !== null) {
            return <div>AND</div>;
        }
    };

    const qualificationSoldHomesRender = (e) => {
        return <div>{renderSoldHomesConditional(e.data.soldHomesOperatorId, e.data.soldHomes)}</div>;
    };

    const qualificationBfiRepaymentsRender = (e) => {
        return <div>{renderSoldHomesConditional(e.data.bfiRepaymentsOperatorId, e.data.bfiRepayments)}</div>;
    };

    const renderSoldHomesConditional = (operatorId, value) => {
        switch (operatorId) {
            case LevelOperatorType.GreaterThan:
                return '> ' + value;
            case LevelOperatorType.GreaterThanOrEqualTo:
                return value + '+';
            case LevelOperatorType.EqualTo:
                return value;
            case LevelOperatorType.LessThan:
                return '< ' + value;
            case LevelOperatorType.LessThanOrEqualTo:
                return '<= ' + value;
        }
    };

    const onCellPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }

        if (e.data.id === currentPrincipalLevel) {
            e.cellElement.className = 'current';
        }
    };

    if (!dataStore) {
        return null;
    }

    return (
        <>
            <div
                className='columns small-12 large-2'
                style={{ textAlign: 'center' }}
            >
                <span>Current Level:</span>
                <br />
                <h3>{currentPrincipalLevelName}</h3>
            </div>
            <div className='columns small-12 large-9'>
                <span
                    style={{
                        height: '24px',
                        width: '24px',
                        marginLeft: 'auto',
                        marginBottom: '5px',
                    }}
                    className='experienceLevelDescriptor'
                >
                    <FontAwesomeIcon
                        icon={faCircleQuestion}
                        style={styles.headerIcon}
                    />
                    <span className='text'>
                        Experience Levels are based on the following:
                        <ul>
                            <li>Construction Experience (Sold Homes)</li>
                            <li>BFI Repayments Experience Last 5 Years</li>
                        </ul>
                    </span>
                </span>
                <BfiDataGrid
                    gridType={GridSettingType.ExperiencesBfiLevelReferenceTable}
                    dataStore={dataStore}
                    applicationInsights={applicationInsights}
                    noDataText='No Level Chart Information found'
                    onCellPrepared={onCellPrepared}
                >
                    <Column
                        caption=''
                        cellRender={levelNameRender}
                        alignment='center'
                        width={120}
                    />
                    <Column
                        alignment='center'
                        headerCellRender={qualificationsHeaderRender}
                    >
                        <Column
                            cellRender={qualificationSoldHomesRender}
                            headerCellRender={soldHomesHeaderRender}
                            alignment='center'
                            width={200}
                        />
                        <Column
                            cellRender={unifierCellRender}
                            caption=''
                            alignment='center'
                            width={100}
                        />
                        <Column
                            cellRender={qualificationBfiRepaymentsRender}
                            headerCellRender={bfiRepaymentsRender}
                            alignment='center'
                            width={200}
                        />
                    </Column>
                    <Column
                        alignment='center'
                        headerCellRender={loanSizeHeaderRender}
                    >
                        <Column
                            dataField='maxLTC'
                            headerCellRender={loanSizeMaxLTCHeaderRender}
                            caption='Maximum LTC'
                            alignment='center'
                            width={200}
                            dataType='number'
                            format={{ type: 'percent', precision: 0 }}
                        />
                        <Column
                            dataField='maxLTV'
                            headerCellRender={loanSizeMaxLTVHeaderRender}
                            caption='Maximum LTV'
                            alignment='center'
                            width={220}
                            dataType='number'
                            format={{ type: 'percent', precision: 0 }}
                        />
                    </Column>
                    <Column
                        dataField='notes'
                        caption=''
                        alignment='left'
                        width={200}
                    />
                </BfiDataGrid>
            </div>
        </>
    );
};

export default BfiLevelReferenceTable;
